import {
  useState,
  useEffect,
  useCallback,
  useRef,
  createContext,
  FC,
  PropsWithChildren,
} from "react";
import { IRequest } from "src/domains";
import {
  queryGetRequests,
  queryGetRequest,
  queryGetActiveRequest,
  queryGetRequestTwilioToken,
  queryGetRequestUnreadCount,
  mutationRequestCreate,
  mutationRequestTimeout,
  mutationRequestCancel,
  mutationRequestReject,
  mutationRequestApprove,
  mutationRequestCallApprove,
  mutationRequestCallReject,
  mutationRequestCallFinish,
  mutationRequestReoffer,
  mutationAccountUpdateLastLogin,
  mutationRequestsRead,
  subscriptionOnRequestSender,
  subscriptionOnRequestOpponent,
  IMutationRequestCreateRequest,
  IMutationRequestTimeoutRequest,
  IMutationRequestCancelRequest,
  IMutationRequestRejectRequest,
  IMutationRequestApproveRequest,
  IMutationRequestCallApproveRequest,
  IMutationRequestCallRejectRequest,
  IMutationRequestCallFinishRequest,
  IMutationRequestReofferRequest,
} from "src/graphql";
import { AppState, AppStateStatus } from "react-native";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import { useCognito, useAuth, AuthState } from "./cognito";
import { navigationRef } from "./navigation";
import {
  CallActionEvent,
  callActionEmit,
  CallReviewCheckEvent,
  callReviewCheckEmit,
  callChatCloseEmit,
  CallKeepAnswerCallEvent,
  CallKeepCancelCallEvent,
  CreateReviewEvent,
  TabChangeEvent,
  LogoutEvent,
  requestReadEmit,
  RequestReadEvent,
  RequestUnreadCountRefreshEvent,
} from "src/utils/event";
import { EventRegister } from "react-native-event-listeners";
import { checkReviewExpected } from "src/utils/call";

export const activeCallState = atom<IRequest | undefined>({
  key: "activeCallState",
  default: undefined,
});

export const activeCallLoadingState = atom<boolean>({
  key: "activeCallLoadingState",
  default: false,
});

export const activeCallErrorState = atom<Error | undefined>({
  key: "activeCallErrorState",
  default: undefined,
});

export const activeCallHiddenState = atom<boolean>({
  key: "activeCallHiddenState",
  default: false,
});

export const requestUnreadCountState = atom<number>({
  key: "requestUnreadCountState",
  default: 0,
});

export const useCallHistory = () => {
  const [items, setItems] = useState<IRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetRequests();
      if (_isMount) {
        setItems(response.data.getRequests?.items || []);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const callActionListener = EventRegister.addEventListener(
      CallActionEvent,
      () => refresh()
    );
    const createReviewListener = EventRegister.addEventListener(
      CreateReviewEvent,
      () => refresh()
    );
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "CallHistoryList") {
          refresh();
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(callActionListener as string);
      EventRegister.removeEventListener(createReviewListener as string);
      EventRegister.removeEventListener(tabChangeListener as string);
    };
  }, [refresh]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const logoutListener = EventRegister.addEventListener(LogoutEvent, () => {
      setItems([]);
      setError(undefined);
    });
    return () => {
      EventRegister.removeEventListener(logoutListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [items, loading, error, refresh] as const;
};

export const useCall = (id?: string) => {
  const [item, setItem] = useState<IRequest | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!id || !_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetRequest({ id });
      if (_isMount) {
        setItem(response.data.getRequest);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const createReviewListener = EventRegister.addEventListener(
      CreateReviewEvent,
      () => refresh()
    );
    return () => {
      EventRegister.removeEventListener(createReviewListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [item, loading, error, refresh] as const;
};

export const useCallActions = () => {
  const [, setActiveCallHidden] = useRecoilState<boolean>(
    activeCallHiddenState
  );

  const createCallRequest = useCallback(
    async (params: IMutationRequestCreateRequest) => {
      await mutationRequestCreate(params);
    },
    []
  );

  const timeoutCallRequest = useCallback(
    async (params: IMutationRequestTimeoutRequest) => {
      await mutationRequestTimeout(params);
      callActionEmit(params.id, "timeout");
    },
    []
  );

  const requestShowCall = useCallback(async () => {
    setActiveCallHidden(false);
  }, []);

  const requestHideCall = useCallback(async () => {
    setActiveCallHidden(true);
  }, []);

  const cancelCallRequest = useCallback(
    async (params: IMutationRequestCancelRequest) => {
      await mutationRequestCancel(params);
      callActionEmit(params.id, "cancel");
    },
    []
  );

  const rejectCallRequest = useCallback(
    async (params: IMutationRequestRejectRequest) => {
      await mutationRequestReject(params);
      callActionEmit(params.id, "reject");
    },
    []
  );

  const approveCallRequest = useCallback(
    async (params: IMutationRequestApproveRequest) => {
      await mutationRequestApprove(params);
      callActionEmit(params.id, "approve");
    },
    []
  );

  const approveCall = useCallback(
    async (params: IMutationRequestCallApproveRequest) => {
      await mutationRequestCallApprove(params);
      callActionEmit(params.id, "callApprove");
    },
    []
  );

  const rejectCall = useCallback(
    async (params: IMutationRequestCallRejectRequest) => {
      await mutationRequestCallReject(params);
      callActionEmit(params.id, "callReject");
    },
    []
  );

  const finishCall = useCallback(
    async (params: IMutationRequestCallFinishRequest) => {
      await mutationRequestCallFinish(params);
      callActionEmit(params.id, "callFinish");
    },
    []
  );

  const requestReofferCall = useCallback(
    async (params: IMutationRequestReofferRequest) => {
      await mutationRequestReoffer(params);
    },
    []
  );

  return {
    createCallRequest,
    timeoutCallRequest,
    requestShowCall,
    requestHideCall,
    cancelCallRequest,
    rejectCallRequest,
    approveCallRequest,
    approveCall,
    rejectCall,
    finishCall,
    requestReofferCall,
  } as const;
};

export const useActiveCall = () => {
  const item = useRecoilValue<IRequest | undefined>(activeCallState);
  const loading = useRecoilValue<boolean>(activeCallLoadingState);
  const error = useRecoilValue<Error | undefined>(activeCallErrorState);

  return [item, loading, error] as const;
};

export const useTwilioToken = (callId?: string) => {
  const [token, setToken] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>();
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (!callId || !_isMount) {
      return;
    }
    if (_isMount) {
      setError(undefined);
    }
    if (_isMount) {
      setLoading(true);
    }
    try {
      const response = await queryGetRequestTwilioToken({ id: callId });
      if (_isMount) {
        setToken(response.data.getRequestTwilioToken);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [callId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [token, loading, error, refresh] as const;
};

export const useRequestUnreadCount = () => {
  const auth = useAuth();
  const [readAction] = useRequestsReadAction();
  const [count, setCount] = useRecoilState<number>(requestUnreadCountState);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();
  let _isMount = true;

  const refresh = useCallback(async () => {
    if (auth !== AuthState.Auth) {
      // Skip on unath
      return;
    }
    if (!_isMount) {
      return;
    }
    if (_isMount) {
      setLoading(true);
    }
    if (_isMount) {
      setError(undefined);
    }
    try {
      const response = await queryGetRequestUnreadCount();
      if (_isMount) {
        setCount(response.data.getRequestUnreadCount || 0);
      }
    } catch (e) {
      console.error(e);
      if (_isMount) {
        setError(e as Error);
      }
    }
    if (_isMount) {
      setLoading(false);
    }
  }, [auth]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    const tabChangeListener = EventRegister.addEventListener(
      TabChangeEvent,
      (e) => {
        if (e.tabName === "CallHistoryList") {
          readAction();
        }
        refresh();
      }
    );
    const requestReadListener = EventRegister.addEventListener(
      RequestReadEvent,
      (e) => {
        refresh();
      }
    );
    const callActionListener = EventRegister.addEventListener(
      CallActionEvent,
      (e) => {
        refresh();
      }
    );
    const requestUnreadCountRefreshListener = EventRegister.addEventListener(
      RequestUnreadCountRefreshEvent,
      (e) => {
        refresh();
      }
    );

    return () => {
      EventRegister.removeEventListener(tabChangeListener as string);
      EventRegister.removeEventListener(requestReadListener as string);
      EventRegister.removeEventListener(callActionListener as string);
      EventRegister.removeEventListener(
        requestUnreadCountRefreshListener as string
      );
    };
  }, [refresh, readAction]);

  useEffect(() => {
    refresh();
  }, [refresh, auth]);

  useEffect(() => {
    const logoutListener = EventRegister.addEventListener(LogoutEvent, () => {
      setCount(0);
      setError(undefined);
    });
    return () => {
      EventRegister.removeEventListener(logoutListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    _isMount = true;
    return () => {
      _isMount = false;
    };
  }, []);

  return [count, loading, error, refresh] as const;
};

export const useRequestsReadAction = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>();

  const handler = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      await mutationRequestsRead();
      requestReadEmit();
      setLoading(false);
      return true;
    } catch (e) {
      console.error(e);
      setError(e as Error);
      setLoading(false);
      return false;
    }
  }, []);

  return [handler, loading, error] as const;
};

type CallType = undefined;

const CallContext = createContext<CallType>(undefined);

export const CallProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const [item, setItem] = useRecoilState<IRequest | undefined>(activeCallState);
  const [, setLoading] = useRecoilState<boolean>(activeCallLoadingState);
  const [, setError] = useRecoilState<Error | undefined>(activeCallErrorState);
  const [activeCallHidden, setActiveCallHidden] = useRecoilState<boolean>(
    activeCallHiddenState
  );
  const auth = useAuth();
  const [cognitoUsername] = useCognito();
  const { rejectCall, approveCall, finishCall } = useCallActions();
  const [appState, setAppState] = useState<AppStateStatus>("active");
  const senderSubscription = useRef<any | undefined>(); // TODO: Replace to ZenObservable.Subscription type
  const opponentSubscription = useRef<any | undefined>(); // TODO: Replace to ZenObservable.Subscription type
  const isMount = useRef<boolean>(true);

  const refresh = useCallback(async () => {
    if (auth !== AuthState.Auth) {
      return;
    }
    if (!isMount.current) {
      return;
    }
    if (isMount.current) {
      setLoading(true);
    }
    if (isMount.current) {
      setError(undefined);
    }
    try {
      const response = await queryGetActiveRequest();
      if (isMount.current) {
        setItem((localItem?: IRequest) => {
          if (!localItem && response.data.getActiveRequest) {
            console.log("activeCallHidden", activeCallHidden);
            if (response.data.getActiveRequest.senderId === cognitoUsername) {
              setTimeout(() => navigationRef.current?.navigate("Call"), 100);
            }
          } else if (localItem && response.data.getActiveRequest) {
            if (
              ["CALL_APPROVED"].indexOf(
                response.data.getActiveRequest.status
              ) !== -1 &&
              activeCallHidden
            ) {
              setTimeout(() => {
                navigationRef.current?.navigate("Call");
              }, 100);
            }
          } else if (localItem && !response.data.getActiveRequest) {
            callChatCloseEmit();
            setTimeout(() => callReviewCheckEmit(localItem.id), 100);
          }
          return response.data.getActiveRequest;
        });
      }
      if (isMount.current) {
        setItem(response.data.getActiveRequest);
      }
    } catch (e) {
      console.error(e);
      if (isMount.current) {
        setError(e as Error);
      }
    }
    if (isMount.current) {
      setLoading(false);
    }
  }, [activeCallHidden, cognitoUsername, auth]);

  const callReviewHandler = useCallback(
    async (callId: string) => {
      if (!cognitoUsername) {
        return;
      }
      try {
        const response = await queryGetRequest({ id: callId });
        if (response.data.getRequest) {
          if (checkReviewExpected(response.data.getRequest, cognitoUsername)) {
            navigationRef.current?.navigate("Review", { callId });
          } else if (
            response.data.getRequest.opponentId === cognitoUsername ||
            ["CANCELED_BY_SENDER", "CALL_FINISHED"].indexOf(
              response.data.getRequest.status
            ) !== -1
          ) {
            navigationRef.current?.navigate("CallHistory", { callId });
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    [cognitoUsername]
  );

  const callKeepAnswerHandler = useCallback(async (callUUID: string) => {
    const response = await queryGetActiveRequest();
    if (
      !response.data.getActiveRequest ||
      ["APPROVED_BY_OPPONENET"].indexOf(
        response.data.getActiveRequest.status
      ) === -1
    ) {
      return;
    }
    try {
      await approveCall({ id: response.data.getActiveRequest.id });
    } catch (e) {
      console.error(e);
    }
  }, []);

  const callKeepCancelHandler = useCallback(
    async (callUUID: string) => {
      const response = await queryGetActiveRequest();
      if (response.data.getActiveRequest) {
        if (
          ["APPROVED_BY_OPPONENET"].indexOf(
            response.data.getActiveRequest.status
          ) !== -1
        ) {
          try {
            await rejectCall({ id: response.data.getActiveRequest.id });
          } catch (e) {
            console.error(e);
          }
        } else if (
          ["CALL_APPROVED"].indexOf(response.data.getActiveRequest.status) !==
          -1
        ) {
          try {
            await finishCall({ id: response.data.getActiveRequest.id });
          } catch (e) {
            console.error(e);
          }
        }
      }
    },
    [item]
  );

  useEffect(() => {
    const callReviewCheckListener = EventRegister.addEventListener(
      CallReviewCheckEvent,
      (e) => callReviewHandler(e.callId)
    );
    const callKeepAnswerCallListener = EventRegister.addEventListener(
      CallKeepAnswerCallEvent,
      (e) => callKeepAnswerHandler(e.callUUID)
    );
    const callKeepCancelCallListener = EventRegister.addEventListener(
      CallKeepCancelCallEvent,
      (e) => callKeepCancelHandler(e.callUUID)
    );
    return () => {
      EventRegister.removeEventListener(callReviewCheckListener as string);
      EventRegister.removeEventListener(callKeepAnswerCallListener as string);
      EventRegister.removeEventListener(callKeepCancelCallListener as string);
    };
  }, [callReviewHandler, callKeepAnswerHandler, callKeepCancelHandler]);

  useEffect(() => {
    const callActionListener = EventRegister.addEventListener(
      CallActionEvent,
      () => refresh()
    );
    return () => {
      EventRegister.removeEventListener(callActionListener as string);
    };
  }, [refresh]);

  useEffect(() => {
    if (senderSubscription.current) {
      senderSubscription.current.unsubscribe();
      senderSubscription.current = undefined;
    }
    if (cognitoUsername && appState === "active") {
      subscriptionOnRequestSender({
        senderId: cognitoUsername,
      })
        .then((observable) => {
          senderSubscription.current = observable.subscribe({
            next: (next) => {
              if (next.value?.data.onRequestSender?.id) {
                callActionEmit(
                  next.value?.data.onRequestSender?.id,
                  "subscriptionOnRequestSender"
                );
              }
            },
            error: (error) => {
              console.log(error);
            },
          });
        })
        .catch((e) => console.error(e));
    }
    return () => {
      if (senderSubscription.current) {
        senderSubscription.current.unsubscribe();
        senderSubscription.current = undefined;
      }
    };
  }, [cognitoUsername, appState]);

  useEffect(() => {
    if (opponentSubscription.current) {
      opponentSubscription.current.unsubscribe();
      opponentSubscription.current = undefined;
    }
    if (cognitoUsername && appState === "active") {
      subscriptionOnRequestOpponent({
        opponentId: cognitoUsername,
      })
        .then((observable) => {
          opponentSubscription.current = observable.subscribe({
            next: (next) => {
              if (next.value?.data.onRequestOpponent?.id) {
                callActionEmit(
                  next.value?.data.onRequestOpponent?.id,
                  "subscriptionOnRequestOpponent"
                );
              }
            },
            error: (error) => {
              console.log(error);
            },
          });
        })
        .catch((e) => console.error(e));
    }
    return () => {
      if (opponentSubscription.current) {
        opponentSubscription.current.unsubscribe();
        opponentSubscription.current = undefined;
      }
    };
  }, [cognitoUsername, appState]);

  // useEffect(() => {
  //   if (!cognitoUser) {
  //     return;
  //   }
  //   const usernameTopic = PubSub.subscribe(cognitoUser.getUsername()).subscribe(
  //     {
  //       next: data => console.log('usernameTopic: Message received', data),
  //       error: error => console.error('usernameTopic:', error),
  //       complete: () => console.log('usernameTopic: Done'),
  //     },
  //   );
  //   return () => {
  //     usernameTopic.unsubscribe();
  //   };
  // }, [cognitoUser]);

  const lastLoginHandler = useCallback(async () => {
    try {
      if (auth === AuthState.Auth) {
        await mutationAccountUpdateLastLogin();
      }
    } catch (e) {
      console.error(e);
    }
  }, [auth]);

  useEffect(() => {
    const interval = setInterval(async () => lastLoginHandler(), 10000);
    return () => {
      clearInterval(interval);
    };
  }, [lastLoginHandler]);

  const onAppStateChange = useCallback(
    (state: AppStateStatus) => {
      setAppState(state);
      if (state === "active") {
        refresh();
      }
    },
    [refresh]
  );

  useEffect(() => {
    AppState.addEventListener("change", onAppStateChange);
    // TODO: Check if it works corrent (muliti hundler)
    // return () => {
    //   AppState.removeEventListener('change', onAppStateChange);
    // };
  }, [onAppStateChange]);

  useEffect(() => {
    isMount.current = true;
    return () => {
      isMount.current = false;
    };
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, auth]);

  return (
    <CallContext.Provider value={undefined}>{children}</CallContext.Provider>
  );
};
